import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apolloClient } from '@/plugins/graphql';
import gql from 'graphql-tag';
import type { ScenarioMapView } from './operationLocation';

export interface OperationConfig {
    id: string;
    name: string;
    mqttBrokerUrl: string;
    mqttTopic: string;
    mqttUseTls: boolean;
    aisTcpUrl: string;
    recordMqttHistory: boolean;
    isTutorial: boolean;
    /** Opts this operation in to the MQTT-archive-backed map view — the
     * time scrubber is bounded to the recording, the map fitBounds to
     * the archive's spatial extent, and positions are queried from
     * archiveObservationsAt as the scrubber moves.  Non-archive
     * operations (default) keep the live-polling behaviour.  Set on
     * the CATS operation via the seeder. */
    archiveEnabled: boolean;
    description: string | null;
    /** Cognito sub of the operation owner when this row is a materialised
     * per-user tutorial instance.  Null on shared (non-tutorial) operations
     * AND on synthetic "not-yet-started" tutorial preview rows — the picker
     * uses this to render a distinct label for those. */
    ownerSub: string | null;
    /** Set on per-user tutorial instances (points at the template row); also
     * set on synthetic tutorial preview rows (in which case `id === templateOperationId`
     * would be misleading — see note in RootQuery.Operations).  For the frontend
     * the important distinction is: ownerSub set = a real instance the user
     * can reset; ownerSub null + isTutorial = synthetic preview, will
     * materialise on first access. */
    templateOperationId: string | null;
    /** Free-form classification banner (e.g. "NATO UNCLASSIFIED"). Rendered
     *  on generated artefacts — currently the plan-player PDF export
     *  prints it at page top + bottom. Null means "no classification
     *  declared" and the artefact renders without a banner. */
    maximumClassification: string | null;
    /** ISO-8601 instant — left edge of the Chart's DVR scrubber and the
     *  cutoff below which stale STANAG-asset icons are hidden. Also the
     *  broker-side age gate: messages with header.time_sent earlier
     *  than this are dropped on receive. Null = no filter, scrubber
     *  falls back to `now - 72h`. */
    mqttMinAcceptedUtc: string | null;
    /** IANA timezone id for operation-local time rendering (Chart time
     *  header, playback-cursor display). Frontend derives the military
     *  zone letter from Intl offset at display time. Null = fall back
     *  to browser locale. */
    operationTimeZone: string | null;
    /** Set on scenario operations (DRDC evaluation scenarios and every
     *  participant's copy): where the map opens and weather is fetched. */
    scenarioMapView: ScenarioMapView | null;
    /** Set on scenario operations: what the map's mission panel says. */
    scenarioBrief: ScenarioBrief | null;
    /** When and where the operation runs, as an admin set it
     *  (updateOperationSettings); every field may be null. */
    settings: OperationSettings | null;
}

export interface OperationSettings {
    periodStartUtc: string | null;
    periodEndUtc: string | null;
    /** The operating area, a GeoJSON Polygon. */
    areaGeoJson: string | null;
    /** Where the map opens. */
    mapView: { lat: number; lon: number; zoom: number } | null;
    /** Where weather and daylight are forecast. */
    forecast: { lat: number; lon: number; label: string } | null;
}

export interface ScenarioBrief {
    role: string;
    name: string;
    situation: string;
    area: string | null;
    isFacilitator: boolean;
}

const WORKSPACES_QUERY = gql`
    query {
        operations {
            id
            name
            mqttBrokerUrl
            mqttTopic
            mqttUseTls
            aisTcpUrl
            recordMqttHistory
            isTutorial
            archiveEnabled
            description
            ownerSub
            templateOperationId
            maximumClassification
            mqttMinAcceptedUtc
            operationTimeZone
            scenarioMapView { lat lon zoom }
            scenarioBrief { role name situation area isFacilitator }
            settings {
                periodStartUtc
                periodEndUtc
                areaGeoJson
                mapView { lat lon zoom }
                forecast { lat lon label }
            }
        }
    }
`;

const RESET_TUTORIAL_MUTATION = gql`
    mutation ResetTutorialOperation($operationId: UUID!) {
        resetTutorialOperation(operationId: $operationId)
    }
`;

export const useOperationStore = defineStore('operation', () => {
    const operations = ref<OperationConfig[]>([]);
    const currentOperationId = ref<string | null>(null);

    const currentOperation = computed(() =>
        operations.value.find(w => w.id === currentOperationId.value) ?? null
    );

    async function fetchOperations() {
        try {
            const result = await apolloClient.query({ query: WORKSPACES_QUERY });
            const fetched: OperationConfig[] = result.data?.operations ?? [];
            operations.value = fetched;

            // If current operation is no longer valid (or not set), pick the first
            if (fetched.length > 0) {
                const stillValid = currentOperationId.value &&
                    fetched.some(w => w.id === currentOperationId.value);
                if (!stillValid) {
                    currentOperationId.value = fetched[0].id;
                }
            }
        } catch (err) {
            console.error('[WorkspaceStore] Failed to fetch operations:', err);
        }
    }

    function setCurrentOperation(id: string) {
        const previous = currentOperationId.value;
        currentOperationId.value = id;
        // Cached results belong to the operation they were fetched for (the
        // X-Operation-Id header isn't part of Apollo's cache key): drop them on
        // a switch, and refetch what's on screen for the new operation.
        if (previous && previous !== id) {
            apolloClient.resetStore().catch((err) => {
                console.warn('[OperationStore] Cache reset after operation switch failed:', err);
            });
        }
    }

    /**
     * Wipe the caller's copy of a tutorial operation on the backend and
     * refresh the local list.  After this returns, the operation's id in
     * the list will have changed (the previous instance is gone; a fresh
     * one will be materialised on the next access), so we also flip the
     * current-operation pointer if the user was sitting on it.
     */
    async function resetTutorialOperation(operationId: string) {
        await apolloClient.mutate({
            mutation: RESET_TUTORIAL_MUTATION,
            variables: { operationId },
        });
        // Remember the *template* the reset was against so we can pick up
        // the freshly-materialised preview row after refetch.  If the reset
        // target had no templateOperationId (shouldn't happen — the backend
        // rejects that case) fall back to the first operation on refresh.
        const priorTemplateId = operations.value
            .find(w => w.id === operationId)?.templateOperationId ?? null;
        await fetchOperations();
        if (currentOperationId.value === operationId) {
            const successor = priorTemplateId
                ? operations.value.find(w => w.templateOperationId === priorTemplateId
                                            || w.id === priorTemplateId)
                : operations.value[0];
            currentOperationId.value = successor?.id ?? operations.value[0]?.id ?? null;
        }
    }

    return {
        operations,
        currentOperationId,
        currentOperation,
        fetchOperations,
        setCurrentOperation,
        resetTutorialOperation,
    };
});
